"use client";

import { useEffect, useState } from "react";

import { useIsLandingPage } from "@/lib/landing/LandingContext";
import { trackEvent } from "@/lib/analytics";
import { EventPreviewModal, type EventPreviewData } from "@/components/landing/EventPreviewModal";
import { ArticlePreviewModal, type ArticlePreviewData } from "@/components/landing/ArticlePreviewModal";

/** Paths whose stories we can render in a preview modal. */
const PREVIEWABLE = /^\/(blog|events|interviews|online-kurse|experten)\//;

type Loaded =
  | { type: "event"; data: EventPreviewData }
  | { type: "article"; data: ArticlePreviewData };

/** The API returns ISO strings; EventPreviewModal wants Date objects. */
type EventPayload = Omit<EventPreviewData, "date" | "endDate"> & {
  date: string | null;
  endDate: string | null;
};

/**
 * Keeps rich-text links on the page.
 *
 * Galleries and cards take `isLanding` into account themselves, but links
 * written into rich text are rendered as raw HTML (see `RichTextField`) and
 * carry no onClick. This listens on the document instead: on a modal-only page
 * it intercepts clicks on internal content links, loads the story and shows it
 * in the same preview modal the galleries use.
 *
 * Anything it cannot preview — external links, unknown paths, modified clicks —
 * is left alone and navigates normally.
 */
export function RichTextLinkInterceptor() {
  const isLanding = useIsLandingPage();
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  useEffect(() => {
    if (!isLanding) return;

    async function onClick(e: MouseEvent) {
      // Let the browser handle new-tab, download and middle-click.
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = (e.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || !PREVIEWABLE.test(href)) return;
      if (anchor.target === "_blank") return;

      // Only content links belong in a preview. Header, footer and any other
      // navigation region must keep navigating: those links point at the same
      // /events/… paths the regex matches, so without this the main nav and the
      // Reisen menu open a modal instead of going anywhere.
      if (anchor.closest("header, footer, nav, [role='navigation']")) return;

      // Galleries and cards already open their own modal — do not double-handle.
      if (anchor.closest("[data-no-preview]")) return;

      e.preventDefault();
      // Also stop React's own listener from running a client-side navigation.
      e.stopPropagation();

      try {
        const res = await fetch(
          `/api/storyblok/preview?slug=${encodeURIComponent(href)}`,
        );
        if (!res.ok) {
          // Nothing to preview — fall back to normal navigation rather than
          // leaving the click dead.
          window.location.href = href;
          return;
        }
        const body = (await res.json()) as
          | { type: "event"; data: EventPayload }
          | { type: "article"; data: ArticlePreviewData };

        if (body.type === "event") {
          // An event without a date cannot be rendered by the modal.
          if (!body.data.date) {
            window.location.href = href;
            return;
          }
          setLoaded({
            type: "event",
            data: {
              ...body.data,
              date: new Date(body.data.date),
              endDate: body.data.endDate ? new Date(body.data.endDate) : undefined,
            },
          });
        } else {
          setLoaded(body);
        }

        trackEvent("content_preview_opened", {
          type: body.type,
          title: body.data.title,
          source: "rich_text_link",
        });
      } catch {
        window.location.href = href;
      }
    }

    // Capture phase: Next.js `<Link>` handles navigation in React's own
    // bubbling listener, so a bubble-phase handler here would run too late —
    // preventDefault would be set, but the client-side navigation already
    // started. Capturing lets us stop it before React sees the event.
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [isLanding]);

  if (!loaded) return null;

  if (loaded.type === "event") {
    return (
      <EventPreviewModal
        event={loaded.data}
        onClose={() => setLoaded(null)}
      />
    );
  }

  return (
    <ArticlePreviewModal
      article={loaded.data}
      onClose={() => setLoaded(null)}
    />
  );
}
